import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ModalStandard from "../../../common/components/Modal/ModalStandard";
import { useAppDispatch, useAppSelector } from "../../../common/state/hooks";
import {
  closeStandardModal,
  getShowStandardModal,
} from "../../../common/state/slice/modal/modalSlice";
import { setAlert } from "../../alert/state/alertsSlice";
import { enqueueOnboardingEvent } from "../events/eventQueue";
import {
  applyOnboardingOps,
  launchTour,
  resetOnboardingState,
  selectOnboardingAudience,
} from "../state/onboardingSlice";
import { CREATOR_WELCOME_TOUR_ID, ENDUSER_WELCOME_TOUR_ID } from "./WelcomeModal";

export const ONBOARDING_RELAUNCH_MODAL_ID = "onboarding-relaunch";

export const RelaunchPrompt = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const audience = useAppSelector(selectOnboardingAudience);
  const showStandardModal = useAppSelector(getShowStandardModal);
  const primaryButtonRef = useRef<HTMLElement | null>(null);
  const isEnduser = audience === "enduser";
  const welcomeTourId = isEnduser ? ENDUSER_WELCOME_TOUR_ID : CREATOR_WELCOME_TOUR_ID;
  const isOpen = showStandardModal.id === ONBOARDING_RELAUNCH_MODAL_ID && showStandardModal.show;
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setBusy(false);
    }
  }, [isOpen]);

  const handlePrimary = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    const resetResult = await dispatch(resetOnboardingState());
    if (!resetOnboardingState.fulfilled.match(resetResult)) {
      dispatch(setAlert({ type: "danger", msg: t("alerts.tutorialResetFail") }));
      dispatch(closeStandardModal(ONBOARDING_RELAUNCH_MODAL_ID));
      return;
    }
    const answerResult = await dispatch(
      applyOnboardingOps([{ op: "relaunchAnswered" }, { op: "welcomeSeen" }]),
    );
    if (!applyOnboardingOps.fulfilled.match(answerResult)) {
      await dispatch(applyOnboardingOps([{ op: "relaunchAnswered" }, { op: "welcomeSeen" }]));
    }
    enqueueOnboardingEvent({
      event: "relaunch_accepted",
      surface: "welcome",
      itemId: welcomeTourId,
      step: null,
    });
    dispatch(closeStandardModal(ONBOARDING_RELAUNCH_MODAL_ID));
    void dispatch(launchTour({ id: welcomeTourId, source: "welcome" }));
  };

  const handleSecondary = () => {
    if (busy) return;
    setBusy(true);
    void dispatch(applyOnboardingOps([{ op: "relaunchAnswered" }])).then((result) => {
      if (!applyOnboardingOps.fulfilled.match(result)) {
        void dispatch(applyOnboardingOps([{ op: "relaunchAnswered" }]));
      }
    });
    enqueueOnboardingEvent({
      event: "relaunch_declined",
      surface: "welcome",
      itemId: null,
      step: null,
    });
    dispatch(closeStandardModal(ONBOARDING_RELAUNCH_MODAL_ID));
  };

  return (
    <ModalStandard
      id={ONBOARDING_RELAUNCH_MODAL_ID}
      className="w-[30rem]"
      clickOnBackgroundClose={false}
      initialFocus={primaryButtonRef}
    >
      <div className="modalContent personalize-scroll-visible">
        <h2 className="pt-3 pb-1 font-medium text-lg text-balance">
          {t("onboarding.relaunch.title")}
        </h2>
        <p className="pb-3 text-gray-600 whitespace-pre-wrap">{t("onboarding.relaunch.body")}</p>
      </div>
      <div className="flex justify-end items-center mt-4 space-x-3">
        <button
          type="button"
          className="btn-alternative-outline"
          onClick={handleSecondary}
          disabled={busy}
        >
          {t("onboarding.relaunch.secondary")}
        </button>
        <button
          ref={primaryButtonRef as React.RefObject<HTMLButtonElement>}
          type="button"
          className="btn-primary-fill"
          onClick={() => {
            void handlePrimary();
          }}
          disabled={busy}
        >
          {t("onboarding.relaunch.primary")}
        </button>
      </div>
    </ModalStandard>
  );
};

export default RelaunchPrompt;
